import React from 'react';

import { Box, Typography } from '@mui/material';
import NavNeededCountBadge from '@sharedCR/common/NavNeededCountBadge';
import { ACTION_CELL_WIDTH_WITH_BADGES } from '@sharedCR/common/SimpleTable/SimpleTable.constants';
import { ENTITY_REFERENCE_TYPE } from 'app/shared/components/TeamChat/TeamChat.entityReferences.utils';
import TeamChatReferenceBadge from 'app/shared/components/TeamChat/TeamChatReferenceBadge';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { incomeMap } from 'constants/options';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { HORIZONTAL_ALIGNMENT } from 'constants/ui';
import { formatDate } from 'utils/date';
import { formatNumberToCurrencyString } from 'utils/number';

import { FINANCIAL_INFO_VIEWS_PATHS } from '../../FinancialInfo.constants';
import ActionButton from '../ActionButton';
import BorrowersNames from '../BorrowersNames';
import {
  actionCellStyles,
  headCellLabelStyles,
  sharedTypographyStyles,
} from '../FinancialInfoBody.styles';

import { wrappedStyles, wrappedTextStyles } from './EmploymentAndIncome.styles';

export const getConfig = ({ isExpanded, onRemove, chatTargets = null, neededCountById = {} }) => ({
  borrower: {
    label: 'Borrower',
    width: isExpanded ? '26.7%' : '18%',
    colSpan: ({ borrower }) => borrower?.colSpan,
    customStyles: ({ borrower }) => borrower?.customStyles,
    content: ({
      borrower,
      modifiedFields: rowModifiedFields,
      pendingChangesActionType: rowPendingChangesActionType,
    }) =>
      borrower?.content ?? (
        <BorrowersNames
          rowModifiedFields={rowModifiedFields}
          rowPendingChangesActionType={rowPendingChangesActionType}
          typographyStyles={wrappedStyles}
          // eslint-disable-next-line
          ownersInfo={[borrower]}
        />
      ),
    fieldName: 'borrowerFullName',
  },
  incomeType: {
    label: (
      <Typography data-truncated sx={headCellLabelStyles} variant="regular14">
        Income Type
      </Typography>
    ),
    width: isExpanded ? '15.9%' : '17.5%',
    content: ({ incomeType }) => (
      <Typography data-truncated sx={wrappedTextStyles} variant="regular14">
        {incomeMap[incomeType]?.name ?? '-'}
      </Typography>
    ),
  },
  employerName: {
    label: 'Employer',
    width: isExpanded ? '26.7%' : '19%',
    content: ({ employerName }) => (
      <Typography data-truncated sx={wrappedStyles} variant="regular14">
        {employerName ?? '-'}
      </Typography>
    ),
  },
  startDate: {
    label: (
      <Typography data-truncated sx={headCellLabelStyles} variant="regular14">
        Start Date
      </Typography>
    ),
    width: isExpanded ? '11.9%' : '15%',
    align: HORIZONTAL_ALIGNMENT.CENTER,
    content: ({ startDate }) => (
      <Typography
        data-truncated
        className="truncate"
        sx={sharedTypographyStyles}
        variant="regular14"
      >
        {startDate ? formatDate(startDate, COMMON_DATE_FORMAT) : '-'}
      </Typography>
    ),
  },
  monthlyIncome: {
    label: (
      <Typography data-truncated sx={headCellLabelStyles} variant="regular14">
        Monthly Income
      </Typography>
    ),
    width: isExpanded ? '11.9%' : '19.5%',
    align: HORIZONTAL_ALIGNMENT.RIGHT,
    content: ({ monthlyIncome }) => (
      <Typography
        data-truncated
        className="truncate"
        sx={sharedTypographyStyles}
        variant="regular14"
      >
        {monthlyIncome ? formatNumberToCurrencyString(monthlyIncome) : '-'}
      </Typography>
    ),
  },
  action: {
    label: 'Action',
    width: ACTION_CELL_WIDTH_WITH_BADGES,
    content: ({ id, pendingChangesActionType }) => {
      const isAdded = pendingChangesActionType === PendingChangesActionType.ADDED;

      return (
        <Box sx={actionCellStyles}>
          <NavNeededCountBadge count={neededCountById[id] ?? 0} />
          <TeamChatReferenceBadge
            chatId={chatTargets?.internalChatId}
            entityId={id}
            entityType={ENTITY_REFERENCE_TYPE.INCOME}
          />
          <ActionButton
            chatTargets={chatTargets}
            entityType={ENTITY_REFERENCE_TYPE.INCOME}
            id={id}
            isHighlighted={isAdded}
            removeText="Delete Income"
            tablePath={FINANCIAL_INFO_VIEWS_PATHS.INCOME_AND_EMPLOYMENT}
            onRemove={onRemove}
          />
        </Box>
      );
    },
    align: HORIZONTAL_ALIGNMENT.CENTER,
  },
});
